import { useState } from "react";
import { communityPosts } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

interface CommunityBoardProps {
  notify: (message: string) => void;
}

export default function CommunityBoard({ notify }: CommunityBoardProps) {
  const [liked, setLiked] = useState<number[]>([]);
  const [draft, setDraft] = useState("");

  const toggleLike = (index: number) => {
    setLiked((prev) =>
      prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
    );
  };

  const handlePublish = () => {
    if (!draft.trim()) {
      notify("先写点内容再发布吧");
      return;
    }
    setDraft("");
    notify("留言已记录，审核通过后会展示在社区板块");
  };

  return (
    <section id="community" className="bg-background-100 py-14 md:py-20 px-4 md:px-10">
      <SectionHeading
        eyebrow="社区讨论"
        title="谁在迷路、哪条线路稳，大家说了算"
        desc="这里汇聚了老用户的实际体验。哪条线路更快、哪个地址有问题，都能在讨论里找到答案。"
      />

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 flex flex-col gap-3">
          {communityPosts.map((post, index) => {
            const isLiked = liked.includes(index);
            return (
              <article
                key={post.name}
                className="rounded-lg bg-background-50 border border-background-200 p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                    <img
                      src={post.avatar}
                      alt={`${post.name} 的头像`}
                      title={`${post.name} 社区用户`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-950">{post.name}</p>
                    <p className="text-xs text-foreground-500">{post.time}</p>
                  </div>
                </div>
                <p className="mt-3.5 text-sm leading-relaxed text-foreground-700">{post.content}</p>
                <div className="mt-4 flex items-center gap-5">
                  <button
                    type="button"
                    onClick={() => toggleLike(index)}
                    className={`inline-flex items-center gap-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      isLiked ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                    }`}
                  >
                    <i className={isLiked ? "ri-heart-fill" : "ri-heart-line"} />
                    {post.likes + (isLiked ? 1 : 0)}
                  </button>
                  <button
                    type="button"
                    onClick={() => notify("回复功能开发中，先点个赞支持一下")}
                    className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line" />
                    回复
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <aside className="lg:col-span-5">
          <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 lg:sticky lg:top-24">
            <div className="flex items-center gap-2.5">
              <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                <i className="ri-edit-2-line text-xl" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-950">发表你的看法</p>
                <p className="text-xs text-foreground-500">聊聊你的使用体验</p>
              </div>
            </div>

            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              maxLength={200}
              rows={5}
              placeholder="哪条线路最快？遇到了什么打不开的情况？"
              className="mt-4 w-full resize-none rounded-md bg-background-100 border border-background-200 px-3.5 py-3 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors scrollbar-thin"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
              <span>友善发言，不发布外链</span>
              <span>{draft.length}/200</span>
            </div>

            <button
              type="button"
              onClick={handlePublish}
              className="mt-4 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              发布留言
            </button>

            <div className="mt-5 pt-5 border-t border-background-200 space-y-2 text-xs text-foreground-600">
              <p className="flex items-center gap-2">
                <i className="ri-group-line text-accent-600" />
                已有 2,318 位吃瓜群众加入讨论
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-time-line text-accent-600" />
                社区每天 10:00 汇总一次反馈
              </p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}